import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertTriangle, CheckCircle, Zap, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatters';

const SECTIONS = ['Basics', 'Features', 'Condition', 'Situation', 'Review'];

const PARKING = [
  { value: 'on_street_permit', label: 'On-street permit' },
  { value: 'allocated_on_street', label: 'Allocated on-street' },
  { value: 'driveway', label: 'Driveway' },
  { value: 'single_garage', label: 'Single garage' },
  { value: 'double_garage', label: 'Double garage' },
];

const GARDEN = [
  { value: 'none', label: 'None' },
  { value: 'communal', label: 'Communal' },
  { value: 'small_private', label: 'Small private' },
  { value: 'medium_private', label: 'Medium private' },
  { value: 'large_private', label: 'Large private' },
];

const ASPECTS = [
  { value: 'unknown', label: 'Unknown' }, { value: 'north', label: 'North' },
  { value: 'east', label: 'East' }, { value: 'south', label: 'South' },
  { value: 'west', label: 'West' }, { value: 'south_west', label: 'South-west' },
];

const HEATING = [
  { value: 'gas_central', label: 'Gas central heating' },
  { value: 'electric', label: 'Electric' },
  { value: 'heat_pump', label: 'Heat pump' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
];

const EXTENSIONS = [
  { value: 'single_storey_rear', label: 'Single storey rear' },
  { value: 'double_storey_rear', label: 'Double storey rear' },
  { value: 'single_storey_side', label: 'Single storey side' },
  { value: 'double_storey_side', label: 'Double storey side' },
  { value: 'loft_conversion', label: 'Loft conversion' },
  { value: 'side_return', label: 'Side return (in-fill)' },
  { value: 'other', label: 'Other' },
];

const CONDITION_ATTRS = [
  { key: 'kitchen', label: 'Kitchen', weight: 'High' },
  { key: 'bathrooms', label: 'Bathrooms', weight: 'Medium-high' },
  { key: 'boiler', label: 'Boiler / Heating System', weight: 'High' },
  { key: 'windows', label: 'Windows', weight: 'Medium' },
  { key: 'roof', label: 'Roof', weight: 'High' },
  { key: 'decoration', label: 'General Decoration', weight: 'Low' },
  { key: 'garden_condition', label: 'Garden Condition', weight: 'Low-medium' },
];

const EMOJIS = [
  { value: 'poor', emoji: '\uD83D\uDE1E', label: 'Poor' },
  { value: 'below_average', emoji: '\uD83D\uDE10', label: 'Below average' },
  { value: 'average', emoji: '\uD83D\uDE42', label: 'Average' },
  { value: 'good', emoji: '\uD83D\uDE0A', label: 'Good' },
  { value: 'excellent', emoji: '\uD83D\uDE04', label: 'Excellent' },
];

const MOTIVATIONS = [
  { value: 'upsizing', label: 'Upsizing' }, { value: 'downsizing', label: 'Downsizing' },
  { value: 'relocating', label: 'Relocating' }, { value: 'financial', label: 'Financial reasons' },
  { value: 'inherited', label: 'Inherited property' }, { value: 'investment_exit', label: 'Investment exit' },
  { value: 'curious', label: 'Just curious' },
];

const TIMELINES = [
  { value: 'asap', label: 'As soon as possible' }, { value: '3_months', label: 'Within 3 months' },
  { value: '6_months', label: 'Within 6 months' }, { value: '12_months', label: 'Within 12 months' },
  { value: 'no_urgency', label: 'No urgency' },
];

const CHAINS = [
  { value: 'no', label: 'No — nothing to buy' }, { value: 'yes_found', label: 'Yes — found a property' },
  { value: 'yes_looking', label: 'Yes — still looking' }, { value: 'unsure', label: 'Not sure' },
];

const OCCUPANCY = [
  { value: 'owner_occupied', label: 'Owner-occupied' }, { value: 'tenanted_ast', label: 'Tenanted — AST' },
  { value: 'tenanted_sitting', label: 'Tenanted — sitting tenant' }, { value: 'vacant', label: 'Vacant' },
];

function SelectGrid({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-lg text-sm transition ${
            value === o.value ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
          }`}>{o.label}</button>
      ))}
    </div>
  );
}

/** Multi-select pill grid. value is an array; toggling adds/removes entries.
 *  Includes a built-in 'None' chip that clears the selection. */
function MultiSelectGrid({ options, value, onChange, noneLabel = 'None' }) {
  const arr = Array.isArray(value) ? value : (value ? [value] : []);
  const isNone = arr.length === 0;
  const toggle = (v) => {
    if (arr.includes(v)) onChange(arr.filter(x => x !== v));
    else onChange([...arr, v]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={() => onChange([])}
        className={`px-3 py-1.5 rounded-lg text-sm transition ${
          isNone ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
        }`}>{noneLabel}</button>
      {options.map(o => {
        const on = arr.includes(o.value);
        return (
          <button key={o.value} type="button" onClick={() => toggle(o.value)}
            className={`px-3 py-1.5 rounded-lg text-sm transition ${
              on ? 'bg-blue-600 text-white ring-1 ring-blue-400' : 'bg-slate-700 text-slate-400 hover:text-white'
            }`}>{o.label}</button>
        );
      })}
    </div>
  );
}

function ConditionInput({ attr, value, onChange }) {
  const v = value || { year_installed: null, condition: 'average' };
  return (
    <div className="bg-slate-800/60 rounded-lg p-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-white font-medium">{attr.label}</span>
        <span className="text-xs text-slate-500">{attr.weight} impact</span>
      </div>
      <div className="flex items-center gap-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Year installed/replaced</label>
          <input type="number" min={1950} max={2026} value={v.year_installed || ''}
            onChange={e => onChange({ ...v, year_installed: parseInt(e.target.value) || null })}
            placeholder="e.g. 2019" className="w-28 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-white placeholder-slate-500" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Condition</label>
          <div className="flex gap-1">
            {EMOJIS.map(e => (
              <button key={e.value} type="button" onClick={() => onChange({ ...v, condition: e.value })}
                title={e.label}
                className={`text-xl px-1 rounded transition ${
                  v.condition === e.value ? 'bg-blue-600/30 ring-1 ring-blue-500' : 'hover:bg-slate-700'
                }`}>{e.emoji}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ValuationWizard({ property, onClose }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [epcData, setEpcData] = useState(null);
  const [epcLoading, setEpcLoading] = useState(true);

  const [answers, setAnswers] = useState({
    basics: {
      bathrooms: property.bathrooms || 1,
      // Multi-select: empty array = no extension
      extensions: [],
    },
    features: {
      // Multi-select: empty array = no parking
      parking: [],
      // Garden split into front + back so a property with "small front
      // and large rear" can be expressed properly. Either can be omitted.
      garden_front: 'none',
      garden_back: 'small_private',
      garden_aspect: 'unknown',
      epc_rating: 'unknown',
      heating: 'gas_central',
    },
    condition: {},
    situation: {
      motivation: 'curious',
      timeline: 'no_urgency',
      chain: 'no',
      occupancy: 'owner_occupied',
    },
    supporting: {
      walkthrough_interest: false,
      notes: '',
    },
  });

  const set = (section, key, value) => {
    setAnswers(a => ({ ...a, [section]: { ...a[section], [key]: value } }));
  };

  // Auto-lookup EPC on mount
  useEffect(() => {
    const token = localStorage.getItem('assetlens_token');
    if (!token || !property?.id) { setEpcLoading(false); return; }
    fetch(`/api/valuation/properties/${property.id}/epc`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.found) {
          setEpcData(data);
          // Pre-populate the EPC rating in features
          if (data.energy_rating) {
            setAnswers(a => ({
              ...a,
              features: { ...a.features, epc_rating: data.energy_rating },
            }));
          }
        }
      })
      .catch(() => {})
      .finally(() => setEpcLoading(false));
  }, [property?.id]);

  const setCondition = (attr, value) => {
    setAnswers(a => ({ ...a, condition: { ...a.condition, [attr]: value } }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('assetlens_token');
      const res = await fetch(`/api/valuation/properties/${property.id}/value`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      if (res.ok) {
        setResult(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Valuation failed');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Result screen
  if (result) {
    const featAdj = result.feature_adjustment || 0;
    const condAdj = result.condition_adjustment || 0;
    const srcLabel = result.avm_source === 'propertydata'
      ? 'an area market baseline'
      : result.avm_source === 'district_median'
        ? 'the local district median of recent sales'
        : 'a local market baseline';

    return (
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
          <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
          <p className="text-slate-400 text-sm mb-2">
            Valuation for {property.postcode}
          </p>
          <h2 className="text-3xl font-bold text-white mb-1">
            {formatCurrency(result.range_low)} — {formatCurrency(result.range_high)}
          </h2>
          <p className="text-emerald-400 font-semibold text-lg">
            Midpoint: {formatCurrency(result.range_mid)}
          </p>
          {result.situation_band && (
            <p className="text-xs text-slate-500 mt-2">{result.situation_band}</p>
          )}
        </div>

        <div className="bg-slate-900 border border-violet-500/25 rounded-2xl p-6 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <TrendingUp size={18} className="text-violet-400" />
            How I reached this valuation
          </h3>
          <ol className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">1</span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">Similar market baseline</p>
                <p>
                  I started from {srcLabel} for {property.postcode}
                  {result.avm_baseline != null ? ` of ${formatCurrency(result.avm_baseline)}` : ''}.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">2</span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">Your property details</p>
                <p>
                  Then I applied what you told me about features
                  ({featAdj >= 0 ? '+' : ''}{formatCurrency(featAdj)}) and condition
                  ({condAdj >= 0 ? '+' : ''}{formatCurrency(condAdj)}).
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">3</span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">Value estimate</p>
                <p>
                  That gives an indicative midpoint of {formatCurrency(result.range_mid)}
                  {' '}(range {formatCurrency(result.range_low)} – {formatCurrency(result.range_high)}).
                </p>
              </div>
            </li>
          </ol>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
          <h3 className="text-white font-semibold mb-3">Breakdown</h3>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">AVM Baseline ({result.avm_source})</span>
            <span className="text-white font-medium">{formatCurrency(result.avm_baseline)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Feature adjustments</span>
            <span className={`font-medium ${result.feature_adjustment >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {result.feature_adjustment >= 0 ? '+' : ''}{formatCurrency(result.feature_adjustment)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Condition adjustments</span>
            <span className={`font-medium ${result.condition_adjustment >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {result.condition_adjustment >= 0 ? '+' : ''}{formatCurrency(result.condition_adjustment)}
            </span>
          </div>
          <div className="border-t border-slate-700 pt-2 flex justify-between text-sm font-bold">
            <span className="text-white">Final Range</span>
            <span className="text-emerald-400">{formatCurrency(result.range_low)} — {formatCurrency(result.range_high)}</span>
          </div>
        </div>

        {/* EPC Upgrade Opportunity */}
        {epcData?.energy_rating && epcData?.potential_rating && epcData.energy_rating !== epcData.potential_rating && (
          <div className="bg-slate-900 border border-blue-500/20 rounded-2xl p-6 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={18} className="text-amber-400" />
              <h3 className="text-white font-semibold">EPC Upgrade Opportunity</h3>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div className="text-center">
                <div className="text-xs text-slate-500">Current</div>
                <div className={`text-3xl font-bold ${
                  ['A','B','C'].includes(epcData.energy_rating) ? 'text-emerald-400' :
                  epcData.energy_rating === 'D' ? 'text-amber-400' : 'text-red-400'
                }`}>{epcData.energy_rating}</div>
              </div>
              <ArrowRight size={20} className="text-slate-600" />
              <div className="text-center">
                <div className="text-xs text-slate-500">Potential</div>
                <div className="text-3xl font-bold text-emerald-400">{epcData.potential_rating}</div>
              </div>
              {epcData.total_cost_low && (
                <div className="ml-auto text-right">
                  <div className="text-xs text-slate-500">Estimated cost</div>
                  <div className="text-sm font-bold text-white">
                    {formatCurrency(epcData.total_cost_low)} — {formatCurrency(epcData.total_cost_high)}
                  </div>
                </div>
              )}
            </div>
            {epcData.recommendations?.length > 0 && (
              <div className="space-y-1.5">
                {epcData.recommendations.slice(0, 5).map((rec, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/60 rounded px-3 py-1.5">
                    <span className="text-xs text-slate-300">{rec.improvement_summary_text || rec.improvement_item}</span>
                    <span className="text-xs text-slate-500 whitespace-nowrap ml-2">
                      {rec.indicative_cost_raw || ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Upgrading from {epcData.energy_rating} to {epcData.potential_rating} could add
              {' '}{formatCurrency((['A','B'].includes(epcData.potential_rating) ? 10000 : 5000) -
                (['E','F','G'].includes(epcData.energy_rating) ? -5000 : 0))} to the property value.
            </p>
          </div>
        )}

        <p className="text-xs text-slate-600 text-center italic">
          This is an indicative estimate only, not a formal valuation. It is based on comparable sales data
          adjusted for the features and condition information you have provided.
          For a formal RICS valuation, please consult a qualified surveyor.
        </p>

        <div className="flex gap-3 justify-center">
          <button onClick={() => { setResult(null); setStep(0); }}
            className="px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition text-sm">
            Recalculate
          </button>
          <button onClick={onClose}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header + progress */}
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-slate-400 hover:text-white transition flex items-center gap-1 text-sm">
          <ArrowLeft size={14} /> Back to portfolio
        </button>
        <div className="flex gap-1">
          {SECTIONS.map((s, i) => (
            <div key={s} className={`h-1.5 w-12 rounded-full ${i <= step ? 'bg-blue-500' : 'bg-slate-700'}`} />
          ))}
        </div>
        <span className="text-xs text-slate-500">Step {step + 1} of {SECTIONS.length}</span>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-2">
        <p className="text-xs text-slate-500 text-center">{property.address_line1}, {property.town}, {property.postcode}</p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>

          {/* Section 1: Basics */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white">Property Basics</h2>
              <p className="text-sm text-slate-400">Confirm or edit the pre-populated details.</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Bathrooms</label>
                  <select value={answers.basics.bathrooms} onChange={e => set('basics', 'bathrooms', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
                    {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Extensions <span className="text-slate-600">(tick all that apply)</span>
                  </label>
                  <MultiSelectGrid options={EXTENSIONS} value={answers.basics.extensions}
                    onChange={v => set('basics', 'extensions', v)}
                    noneLabel="No extension" />
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Features */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white">Key Features</h2>

              {/* EPC Auto-lookup panel */}
              {epcLoading ? (
                <div className="bg-slate-800/60 rounded-lg p-4 text-sm text-slate-400">Looking up EPC data...</div>
              ) : epcData ? (
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap size={16} className="text-amber-400" />
                    <span className="text-sm font-semibold text-white">EPC Certificate Found</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-500 mb-1">Current</div>
                      <div className={`text-2xl font-bold ${
                        ['A','B','C'].includes(epcData.energy_rating) ? 'text-emerald-400' :
                        epcData.energy_rating === 'D' ? 'text-amber-400' : 'text-red-400'
                      }`}>{epcData.energy_rating || '?'}</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-500 mb-1">Potential</div>
                      <div className="text-2xl font-bold text-emerald-400">{epcData.potential_rating || '?'}</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-500 mb-1">Floor Area</div>
                      <div className="text-lg font-bold text-white">{epcData.floor_area_sqm ? `${epcData.floor_area_sqm}m²` : '?'}</div>
                    </div>
                  </div>

                  {!epcData.is_compliant && (
                    <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-red-300">
                        This property is below EPC E — it cannot be legally rented without improvements.
                        {epcData.compliance_cost_low && (
                          <> Estimated cost to reach E: {formatCurrency(epcData.compliance_cost_low)} — {formatCurrency(epcData.compliance_cost_high)}</>
                        )}
                      </span>
                    </div>
                  )}

                  {epcData.recommendations?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        <TrendingUp size={14} className="text-blue-400" />
                        <span className="text-xs font-semibold text-slate-300">
                          Improvements to reach {epcData.potential_rating}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {epcData.recommendations.slice(0, 6).map((rec, i) => (
                          <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded px-3 py-1.5">
                            <span className="text-xs text-slate-300">{rec.improvement_summary_text || rec.improvement_item}</span>
                            <span className="text-xs text-slate-500 whitespace-nowrap ml-2">
                              {rec.indicative_cost_raw || (rec.indicative_cost_low ? `£${rec.indicative_cost_low}-£${rec.indicative_cost_high}` : '')}
                            </span>
                          </div>
                        ))}
                      </div>
                      {epcData.total_cost_low && (
                        <div className="text-right text-xs text-slate-400 mt-2">
                          Total: {formatCurrency(epcData.total_cost_low)} — {formatCurrency(epcData.total_cost_high)}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-slate-600">
                    Inspected: {epcData.inspection_date || 'Unknown'}.
                    Rating pre-populated below — override if you've had a new assessment.
                  </p>
                </div>
              ) : (
                <div className="bg-slate-800/60 rounded-lg p-3 text-xs text-slate-500">
                  No EPC certificate found for this address. Please select your rating below.
                </div>
              )}

              {/* Parking — multi-select (driveway + single garage etc.) */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Parking <span className="text-slate-600">(tick all that apply)</span>
                </label>
                <MultiSelectGrid options={PARKING} value={answers.features.parking}
                  onChange={v => set('features', 'parking', v)} noneLabel="No parking" />
              </div>

              {/* Garden — split front and back so "small front + large rear" works */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Front garden</label>
                <SelectGrid options={GARDEN} value={answers.features.garden_front}
                  onChange={v => set('features', 'garden_front', v)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Back / rear garden</label>
                <SelectGrid options={GARDEN} value={answers.features.garden_back}
                  onChange={v => set('features', 'garden_back', v)} />
              </div>

              {[
                { label: 'Garden Aspect', key: 'garden_aspect', options: ASPECTS },
                { label: 'EPC Rating', key: 'epc_rating', options: ['A','B','C','D','E','F','G','unknown'].map(v => ({ value: v, label: v === 'unknown' ? 'Unknown' : v })) },
                { label: 'Heating', key: 'heating', options: HEATING },
              ].map(({ label, key, options }) => (
                <div key={key}>
                  <label className="text-xs text-slate-500 block mb-1">
                    {label}
                    {key === 'epc_rating' && epcData?.energy_rating && (
                      <span className="text-blue-400 ml-1">(auto-populated from EPC records)</span>
                    )}
                  </label>
                  <SelectGrid options={options} value={answers.features[key]} onChange={v => set('features', key, v)} />
                </div>
              ))}
            </div>
          )}

          {/* Section 3: Condition */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white">Condition Scoring</h2>
              <p className="text-sm text-slate-400">
                Enter the year each item was last replaced and rate its current condition.
              </p>
              {CONDITION_ATTRS.map(attr => (
                <ConditionInput key={attr.key} attr={attr}
                  value={answers.condition[attr.key]}
                  onChange={v => setCondition(attr.key, v)} />
              ))}
            </div>
          )}

          {/* Section 4: Situation */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white">Your Situation</h2>
              <p className="text-sm text-slate-400">This affects the confidence range, not the market value estimate.</p>
              {[
                { label: 'Why are you considering selling?', key: 'motivation', options: MOTIVATIONS },
                { label: 'What is your timeline?', key: 'timeline', options: TIMELINES },
                { label: 'Are you in a chain?', key: 'chain', options: CHAINS },
                { label: 'Is the property currently occupied?', key: 'occupancy', options: OCCUPANCY },
              ].map(({ label, key, options }) => (
                <div key={key}>
                  <label className="text-xs text-slate-500 block mb-1">{label}</label>
                  <SelectGrid options={options} value={answers.situation[key]} onChange={v => set('situation', key, v)} />
                </div>
              ))}
            </div>
          )}

          {/* Section 5: Review + Submit */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white">Review & Calculate</h2>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-200">
                    The accuracy of your indicative value depends on the accuracy of your answers.
                    Overstating condition or features will produce an inflated estimate that won't reflect
                    what buyers will offer. We're not here to flatter your property — we're here to give
                    you a useful number.
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500 block mb-1">Additional notes (optional)</label>
                <textarea value={answers.supporting.notes} onChange={e => set('supporting', 'notes', e.target.value)}
                  maxLength={500} rows={3} placeholder="Anything else about the property..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 resize-none" />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={answers.supporting.walkthrough_interest}
                    onChange={e => set('supporting', 'walkthrough_interest', e.target.checked)}
                    className="rounded border-slate-600" />
                  I'm interested in a 360° virtual walkthrough
                </label>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-slate-800">
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
          className="flex items-center gap-1 px-4 py-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 transition">
          <ArrowLeft size={14} /> Back
        </button>

        {step < SECTIONS.length - 1 ? (
          <button onClick={() => setStep(s => s + 1)}
            className="flex items-center gap-1 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium">
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium disabled:opacity-50">
            {submitting ? 'Calculating...' : 'Calculate My Indicative Value'}
          </button>
        )}
      </div>
    </div>
  );
}
